///
/// EarthBrowser
///
/// Created by Matt Giger on December 24, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { Database } from '@/earthbrowser/database/Database'
import { type VectorTileId } from '@/earthbrowser/vector-tiles/VectorTileId'

// type VectorTileCacheRow = {
// 	tile_key: string
// 	z: number
// 	x: number
// 	y: number
// 	source: string
// 	data: Uint8Array
// 	size: number
// 	expires: number
// 	created: number
// 	last_accessed: number
// 	access_count: number
// }

type VectorTileCacheInsert = {
	tile_key: string
	z: number
	x: number
	y: number
	source: string
	data: Uint8Array
	size: number
	expires: number
	created: number
}

/**
 * VectorTileCacheTable - Manages caching of MapLibre Vector Tiles in SQLite
 *
 * Features:
 * - Efficient storage and retrieval of MVT tiles
 * - Per-source caching (multiple tile sources supported)
 * - Expiration management
 * - LRU-style access tracking
 * - Storage size monitoring
 */
export class VectorTileCacheTable {

	/**
	 * Initialize the vector tile cache table and indexes
	 */
	public static initialize(db: Database) {
		db.enqueue([
			{
				sql: `create table if not exists vector_tile_cache (
					tile_key text not null,
					z integer not null,
					x integer not null,
					y integer not null,
					source text not null,
					data blob not null,
					size integer not null,
					expires integer not null,
					created integer not null,
					last_accessed integer not null default 0,
					access_count integer not null default 0,
					primary key (tile_key, source)
				)`
			},
			{ sql: "create index if not exists vector_tile_cache_source_zxy on vector_tile_cache (source, z, x, y)" },
			{ sql: "create index if not exists vector_tile_cache_expires on vector_tile_cache (expires)" },
			{ sql: "create index if not exists vector_tile_cache_accessed on vector_tile_cache (last_accessed)" },
			{ sql: "create index if not exists vector_tile_cache_source on vector_tile_cache (source)" }
		], "critical")
	}

	/**
	 * Get the total size of all cached vector tiles
	 */
	public static async cacheSize(source?: string): Promise<number> {
		if (Database.cacheAvailable) {
			const sql = source
				? "select sum(size) as total from vector_tile_cache where source = ?"
				: "select sum(size) as total from vector_tile_cache"
			const params = source ? [source] : []
			const rows = await Database.db.exec(sql, params, "high") as { total: number | null }[]
			return rows[0]?.total ?? 0
		}
		return -1
	}

	/**
	 * Get the number of cached tiles
	 */
	public static async tileCount(source?: string): Promise<number> {
		if (Database.cacheAvailable) {
			const sql = source
				? "select count(*) as count from vector_tile_cache where source = ?"
				: "select count(*) as count from vector_tile_cache"
			const params = source ? [source] : []
			const rows = await Database.db.exec(sql, params, "high") as { count: number }[]
			return rows[0]?.count ?? 0
		}
		return -1
	}

	/**
	 * Get a vector tile from cache
	 */
	public static async get(tileId: VectorTileId, source: string): Promise<ArrayBuffer | undefined> {
		if (Database.cacheAvailable) {
			const now = Date.now()
			const rows = await Database.db.exec(
				"select data from vector_tile_cache where tile_key = ? and source = ? and expires > ?",
				[tileId.key, source, now],
				"high"
			) as { data: Uint8Array }[]

			if (rows.length > 0) {
				// Update access tracking (fire and forget)
				Database.db.enqueue([{
					sql: "update vector_tile_cache set last_accessed = ?, access_count = access_count + 1 where tile_key = ? and source = ?",
					params: [now, tileId.key, source]
				}], "low")

				return rows[0].data.buffer as ArrayBuffer
			}
		}
		return undefined
	}

	/**
	 * Get multiple vector tiles from cache
	 */
	public static async getMany(tileIds: VectorTileId[], source: string): Promise<Map<string, ArrayBuffer>> {
		const result = new Map<string, ArrayBuffer>()

		if (Database.cacheAvailable && tileIds.length > 0) {
			const now = Date.now()
			const keys = tileIds.map(t => t.key)
			const placeholders = keys.map(() => '?').join(',')

			const rows = await Database.db.exec(
				`select tile_key, data from vector_tile_cache where source = ? and tile_key in (${placeholders}) and expires > ?`,
				[source, ...keys, now],
				"high"
			) as { tile_key: string, data: Uint8Array }[]

			for (const row of rows) {
				result.set(row.tile_key, row.data.buffer as ArrayBuffer)
			}

			// Update access tracking for found tiles (fire and forget)
			if (rows.length > 0) {
				const foundKeys = rows.map(r => r.tile_key)
				const updatePlaceholders = foundKeys.map(() => '?').join(',')
				Database.db.enqueue([{
					sql: `update vector_tile_cache set last_accessed = ?, access_count = access_count + 1 where source = ? and tile_key in (${updatePlaceholders})`,
					params: [now, source, ...foundKeys]
				}], "low")
			}
		}

		return result
	}

	/**
	 * Store a vector tile in cache
	 */
	public static set(tileId: VectorTileId, source: string, data: ArrayBuffer, expires: number): void {
		if (Database.cacheAvailable) {
			const now = Date.now()
			const entry: VectorTileCacheInsert = {
				tile_key: tileId.key,
				z: tileId.z,
				x: tileId.x,
				y: tileId.y,
				source,
				data: new Uint8Array(data),
				size: data.byteLength,
				expires,
				created: now
			}

			const statement = `
				insert into vector_tile_cache (tile_key, z, x, y, source, data, size, expires, created, last_accessed, access_count)
				values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
				on conflict(tile_key, source) do update set
					data = excluded.data,
					size = excluded.size,
					expires = excluded.expires,
					created = excluded.created,
					last_accessed = excluded.last_accessed,
					access_count = 0
			`

			Database.db.enqueue([{
				sql: statement,
				params: [
					entry.tile_key,
					entry.z,
					entry.x,
					entry.y,
					entry.source,
					entry.data,
					entry.size,
					entry.expires,
					entry.created,
					now,
					0
				]
			}], "low")
		}
	}

	/**
	 * Store multiple vector tiles in cache
	 */
	public static setMany(tiles: Array<{ tileId: VectorTileId, source: string, data: ArrayBuffer, expires: number }>): void {
		if (Database.cacheAvailable && tiles.length > 0) {
			const now = Date.now()
			const statement = `
				insert into vector_tile_cache (tile_key, z, x, y, source, data, size, expires, created, last_accessed, access_count)
				values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
				on conflict(tile_key, source) do update set
					data = excluded.data,
					size = excluded.size,
					expires = excluded.expires,
					created = excluded.created,
					last_accessed = excluded.last_accessed,
					access_count = 0
			`

			const commands = tiles.map(tile => ({
				sql: statement,
				params: [
					tile.tileId.key,
					tile.tileId.z,
					tile.tileId.x,
					tile.tileId.y,
					tile.source,
					new Uint8Array(tile.data),
					tile.data.byteLength,
					tile.expires,
					now,
					now,
					0
				]
			}))

			Database.db.enqueue(commands, "low")
		}
	}

	/**
	 * Remove a specific tile from cache
	 */
	public static remove(tileId: VectorTileId, source: string): void {
		if (Database.cacheAvailable) {
			Database.db.enqueue([{
				sql: "delete from vector_tile_cache where tile_key = ? and source = ?",
				params: [tileId.key, source]
			}], "low")
		}
	}

	/**
	 * Remove all tiles for a specific source
	 */
	public static clearSource(source: string): void {
		if (Database.cacheAvailable) {
			Database.db.enqueue([{
				sql: "delete from vector_tile_cache where source = ?",
				params: [source]
			}], "low")
		}
	}

	/**
	 * Remove all expired tiles
	 */
	public static purgeExpired(): void {
		if (Database.cacheAvailable) {
			Database.db.enqueue([{
				sql: "delete from vector_tile_cache where expires < ?",
				params: [Date.now()]
			}], "low")
		}
	}

	/**
	 * Remove least recently used tiles to free up space
	 * @param maxSizeBytes - Maximum cache size in bytes
	 * @param source - Optional: only prune specific source
	 */
	public static async pruneLRU(maxSizeBytes: number, source?: string): Promise<number> {
		if (Database.cacheAvailable) {
			// Get current size
			const currentSize = await VectorTileCacheTable.cacheSize(source)

			if (currentSize > maxSizeBytes) {
				const bytesToFree = currentSize - maxSizeBytes

				// Delete least recently accessed tiles until we're under the limit
				const sql = source
					? `delete from vector_tile_cache where source = ? and tile_key in (
						select tile_key from vector_tile_cache where source = ?
						order by last_accessed asc, access_count asc
						limit (select count(*) from vector_tile_cache where source = ?) / 4
					)`
					: `delete from vector_tile_cache where tile_key in (
						select tile_key from vector_tile_cache
						order by last_accessed asc, access_count asc
						limit (select count(*) from vector_tile_cache) / 4
					)`

				const params = source ? [source, source, source] : []
				await Database.db.exec(sql, params, "low")

				return bytesToFree
			}
		}
		return 0
	}

	/**
	 * Get cache statistics
	 */
	public static async getStats(source?: string): Promise<{
		tileCount: number
		totalSize: number
		avgTileSize: number
		oldestTile: number
		newestTile: number
	}> {
		if (Database.cacheAvailable) {
			const sql = source
				? `select
					count(*) as tile_count,
					sum(size) as total_size,
					avg(size) as avg_tile_size,
					min(created) as oldest_tile,
					max(created) as newest_tile
				from vector_tile_cache where source = ?`
				: `select
					count(*) as tile_count,
					sum(size) as total_size,
					avg(size) as avg_tile_size,
					min(created) as oldest_tile,
					max(created) as newest_tile
				from vector_tile_cache`

			const params = source ? [source] : []
			const rows = await Database.db.exec(sql, params, "high") as Array<{
				tile_count: number
				total_size: number | null
				avg_tile_size: number | null
				oldest_tile: number | null
				newest_tile: number | null
			}>

			const row = rows[0]
			return {
				tileCount: row.tile_count,
				totalSize: row.total_size ?? 0,
				avgTileSize: row.avg_tile_size ?? 0,
				oldestTile: row.oldest_tile ?? 0,
				newestTile: row.newest_tile ?? 0
			}
		}

		return {
			tileCount: 0,
			totalSize: 0,
			avgTileSize: 0,
			oldestTile: 0,
			newestTile: 0
		}
	}

	/**
	 * Clear all vector tile cache
	 */
	public static clear(): void {
		if (Database.cacheAvailable) {
			Database.db.enqueue([{
				sql: "delete from vector_tile_cache"
			}], "low")
		}
	}
}

